import { TransactionMessage } from "@autorujira/workflow-launcher";
import { useEffect, useMemo, useState } from "react";
import { graphql, useFragment, useRelayEnvironment } from "react-relay";
import { Disposable, requestSubscription } from "relay-runtime";
import { Msg, MsgAuthzGrant, MsgExecute } from "rujira.js";
import { useTranslation } from "rujira.ui";
import { useWorkflowLauncher } from "../../hooks/useWorkflowLauncher";
import { usePreloadedAccountData } from "../../services/accountData";
import { useAccounts } from "../../services/accounts";
import { useTradeContext } from "../components/Context";
import type { useAutoClaimerCreatedSubscription } from "./__generated__/useAutoClaimerCreatedSubscription.graphql";
import type {
  useAutoClaimerFragment$data,
  useAutoClaimerFragment$key,
} from "./__generated__/useAutoClaimerFragment.graphql";
import type { useAutoClaimerUpdatedSubscription } from "./__generated__/useAutoClaimerUpdatedSubscription.graphql";

export const useAutoClaimerFragment = graphql`
  fragment useAutoClaimerFragment on Account {
    address
    auto {
      workflows(states: ["RUNNING"], first: 100000)
        @connection(key: "useAutoClaimerFragment_workflows") {
        __id
        edges {
          node {
            id
            instanceId
            workflowId
            requester
            executionType
            cronExpression
            onchainParameters
            offchainParameters
            expirationDate
            launcherId
            state
            workflow {
              id
              name
            }
            schedule {
              status
              nextExecutionTimes
              runningWorkflows
            }
            lastRun {
              state
              startTime
              endTime
            }
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

const useAutoClaimerUpdatedSubscription = graphql`
  subscription useAutoClaimerUpdatedSubscription($id: ID!) {
    node(id: $id) {
      __typename
      ... on AutoWorkflowInstance {
        id
        instanceId
        workflowId
        requester
        executionType
        cronExpression
        onchainParameters
        offchainParameters
        expirationDate
        launcherId
        state
        workflow {
          id
          name
        }
        schedule {
          status
          nextExecutionTimes
          runningWorkflows
        }
        lastRun {
          state
          startTime
          endTime
        }
      }
    }
  }
`;

const useAutoClaimerCreatedSubscription = graphql`
  subscription useAutoClaimerCreatedSubscription(
    $owner: Address!
    $connection: ID!
  ) {
    autoInstanceCreated(owner: $owner) @appendEdge(connections: [$connection]) {
      cursor
      node {
        ... on AutoWorkflowInstance {
          id
          instanceId
          workflowId
          requester
          executionType
          cronExpression
          onchainParameters
          offchainParameters
          expirationDate
          launcherId
          state
          workflow {
            id
            name
          }
          schedule {
            status
            nextExecutionTimes
            runningWorkflows
          }
          lastRun {
            state
            startTime
            endTime
          }
        }
      }
    }
  }
`;

type ActiveInstance = NonNullable<
  NonNullable<
    NonNullable<useAutoClaimerFragment$data["auto"]>["workflows"]["edges"]
  >[number]
>["node"];

export interface AutoClaimer {
  launcherId?: string;
  activeInstance: ActiveInstance | null;
  msgs: Msg[] | "loading";
}

export const useAutoClaimer = (): AutoClaimer => {
  const { base, quote, address } = useTradeContext();
  const { t } = useTranslation();
  const { launchWorkflow, cancelWorkflow } = useWorkflowLauncher();
  type Result = Awaited<
    ReturnType<typeof launchWorkflow> | ReturnType<typeof cancelWorkflow>
  >;
  const [result, setResult] = useState<Result>();
  const { selected } = useAccounts();
  const env = useRelayEnvironment();

  const { accountData } = usePreloadedAccountData();

  const account = useFragment<useAutoClaimerFragment$key>(
    useAutoClaimerFragment,
    accountData || null
  );

  const launcherId = useMemo(() => {
    if (!base || !quote) return undefined;
    return `RUJI_TRADE_${base}_${quote}`;
  }, [base, quote]);

  const activeInstance: null | ActiveInstance = useMemo(() => {
    if (!account?.auto?.workflows?.edges) {
      return null;
    }
    const workflows = account.auto.workflows.edges;
    const matchingWorkflow = workflows?.find(
      (edge) =>
        edge?.node?.launcherId === launcherId && edge?.node?.state === "RUNNING"
    );
    return matchingWorkflow?.node || null;
  }, [account, launcherId]);

  useEffect(() => {
    if (!selected || !launcherId || !address || !base || !quote) {
      return;
    }

    let subscription: Disposable | null = null;

    if (activeInstance?.id) {
      subscription = requestSubscription<useAutoClaimerUpdatedSubscription>(
        env,
        {
          subscription: useAutoClaimerUpdatedSubscription,
          variables: { id: activeInstance.id },
        }
      );
    } else if (
      selected?.address &&
      launcherId &&
      account?.auto?.workflows?.__id
    ) {
      subscription = requestSubscription<useAutoClaimerCreatedSubscription>(
        env,
        {
          subscription: useAutoClaimerCreatedSubscription,
          variables: {
            owner: selected.address,
            connection: account.auto.workflows.__id,
          },
        }
      );
    }

    setResult(undefined);
    (activeInstance?.instanceId
      ? cancelWorkflow({
          instanceId: activeInstance.instanceId,
          onlyGenerateMessages: true,
        })
      : launchWorkflow({
          workflowName: "AutoClaimer RUJI Trade",
          appId: "RUJI-Trade",
          title: `${t("AutoClaim")} ${base}-${quote} ${t("filled orders")}`,
          executionType: "Recurrent",
          cronExpression: "0 * * * *",
          onlyGenerateMessages: true,
          parameters: {
            onchain_parameters: {
              claimContractAddress: { string: address },
            },
            offchain_parameters: {
              claimTemplateId: { string: "fin" },
              notificationType: { string: "telegram" },
              minAmountToClaim: { string: "5" },
              launcherId: { string: launcherId },
            },
          },
        })
    ).then(setResult);

    return () => {
      if (subscription) {
        subscription.dispose();
      }
    };
  }, [activeInstance]);

  const msgs = useMemo(() => {
    if (!selected) return [];
    if (!result) return "loading";
    if ("messages" in result)
      return result.messages?.map(mapMessage(selected.address)) || [];
    throw new Error(`Unhandled Auto result`);
  }, [activeInstance, result, selected]);

  return {
    launcherId,
    activeInstance,
    msgs,
  };
};

const mapMessage =
  (address: string) =>
  (msg: TransactionMessage): Msg => {
    const msgType = msg.typeUrl;

    switch (msgType) {
      case "/cosmos.authz.v1beta1.MsgGrant":
        return new MsgAuthzGrant(
          { address, network: "THOR" },
          msg.value.granter,
          msg.value.grantee,
          "/cosmwasm.wasm.v1.MsgExecuteContract"
        );

      case "/cosmwasm.wasm.v1.MsgExecuteContract": {
        const msgBody =
          msg.readableValue?.msg ||
          JSON.parse(new TextDecoder().decode(msg.value.msg));

        return new MsgExecute(
          { address, network: "THOR" },
          msg.value.funds || [],
          msg.value.contract,
          msgBody
        );
      }

      default:
        throw new Error(`Unsupported Msg type ${msgType}`);
    }
  };
